import React from 'react';
import { useT } from '../../i18n/LanguageContext';
import { sizeLabel } from './SizePreferences';

// Area bands are illustrative allocations, not intact sheet shapes or cutting instructions.
export function coverageBands(pieces, target) {
  let remaining = Math.round(target * 10000);
  return pieces.map((piece) => {
    const supplied = Math.round(piece.areaCm2 * 10000) * piece.quantity;
    const used = Math.min(remaining, supplied);
    remaining = Math.max(0, remaining - used);
    return { used: used / 10000, excess: (supplied - used) / 10000 };
  });
}
// Keep a nonzero allocation visible, including very small fractions of a large target.
function allocationPercentage(allocated, requested) {
  const percentage = (allocated / requested) * 100;
  const rounded = Number(percentage.toFixed(2));
  if (allocated > 0 && rounded === 0) return '<0.01%';
  if (allocated < requested && rounded === 100) return '>99.99%';
  return `${rounded}%`;
}

export default function SizeCombinationPreview({ result, length, width }) {
  const t = useT();
  const dimensions = Number(length) > 0 && Number(width) > 0;
  const l = dimensions ? Number(length) : Math.sqrt(result.requestedAreaCm2);
  const w = dimensions ? Number(width) : l;
  const scale = Math.min(280 / l, 160 / w);
  const rectWidth = l * scale;
  const rectHeight = w * scale;
  const x = (360 - rectWidth) / 2;
  const y = 30;
  const colors = [
    '#bddfff',
    '#aee4d6',
    '#e0cef5',
    '#ffe0a8',
    '#f0a9be',
    '#d2df91',
    '#b5c7d8',
    '#d8b29b',
  ];
  const bands = coverageBands(result.pieces, result.requestedAreaCm2);
  let offset = 0;
  return (
    <figure className="rc-combination-preview">
      <figcaption>{t('Illustrative coverage layout — trimming may be needed')}</figcaption>
      <svg
        viewBox="0 0 360 235"
        role="img"
        aria-label={t('Combined area illustration with a dotted measurement outline')}
      >
        {bands.map((band, index) => {
          const height = (band.used / result.requestedAreaCm2) * rectHeight;
          const top = y + offset;
          offset += height;
          return band.used > 0 ? (
            <g key={index}>
              <rect
                x={x}
                y={top}
                width={rectWidth}
                height={height}
                fill={colors[index % colors.length]}
              />
              {height >= 22 && rectWidth >= 140 && (
                <text
                  x="180"
                  y={top + height / 2 + 4}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#0f172a"
                >
                  ({result.pieces[index].quantity}) {sizeLabel(result.pieces[index].sizeToken)}
                </text>
              )}
            </g>
          ) : null;
        })}
        <rect
          x={x - 1}
          y={y - 1}
          width={rectWidth + 2}
          height={rectHeight + 2}
          fill="none"
          stroke="#334155"
          strokeWidth="2"
          strokeDasharray="5 4"
        />
        <text x="180" y="18" textAnchor="middle" fontSize="12" fill="#334155">
          {dimensions ? `${length} × ${width} cm` : t('Area-only reference square')}
        </text>
        <text x="180" y={y + rectHeight + 23} textAnchor="middle" fontSize="12" fill="#334155">
          {result.requestedAreaCm2} cm²
        </text>
      </svg>
      <ul className="rc-preview-key">
        {result.pieces.map((piece, index) => (
          <li key={piece.productId}>
            <span aria-hidden="true" style={{ background: colors[index % colors.length] }} />
            <span>
              ({piece.quantity}) {sizeLabel(piece.sizeToken)}
              {' — '}
              {t('{area} cm² allocated ({percentage} of requested area)', {
                area: bands[index].used,
                percentage: allocationPercentage(bands[index].used, result.requestedAreaCm2),
              })}
              {bands[index].excess > 0
                ? ` — ${t('{area} cm² excess', { area: Number(bands[index].excess.toFixed(4)) })}`
                : ''}
            </span>
          </li>
        ))}
      </ul>
      <p className="rc-muted">
        {t(
          'Colors show area allocated from each size, not intact sheet shapes. Multiple cuts may be needed. This is not a cutting guide or a guarantee of wound coverage.'
        )}
      </p>
    </figure>
  );
}
